import React, { useContext, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Button,
    Container as MaterialContainer,
    Grid,
    UiDialog as Dialog,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import { HeaderContainer, TitleIcon } from 'common/components';
import * as customColumnsSelectors from 'common/modules/customColumns/selectors';
import { LoadingContext } from 'common/utils/contexts';

import createContactModal from '../createContactModal';
import * as createContactModalActions from '../createContactModal/actions';

import * as actions from './actions';
import Content from './components/Content';
import ReasignForm from './components/ReasignForm';
import * as selectors from './selectors';

const Container = ({
    closeReasignModal,
    contacts,
    downloadFile,
    filterContacts,
    handleClickBulkItems,
    handleClickCreate,
    handleSaveRow,
    initialize,
    isDownloading,
    isFetchingContacts,
    isFetchingContactsToReasign,
    isInitializing,
    isOpenReasignModal,
    isUploading,
    paginationData,
    reset,
    showEmptyState,
    tableSettings,
    uploadFile,
    users,
}) => {
    const { t } = useTranslation();
    const loadingContext = useContext(LoadingContext);
    const [filterData, setFilterData] = useState({
        orderBy: 'created_at',
        page: 0,
        pageSize: 25,
        searchText: '',
        sortBy: 'desc',
        status: 'active',
        userId: '',
    });

    useEffect(() => {
        initialize(filterData);
        return () => reset();
    }, []);

    useEffect(() => {
        if (isEmpty(loadingContext)) return;
        if (isDownloading)
            loadingContext.openLoading(t('Downloading document').concat('...'));
        else loadingContext.closeLoading();
    }, [isDownloading]);

    useEffect(() => {
        if (isEmpty(loadingContext)) return;
        if (isUploading)
            loadingContext.openLoading(t('Loading document').concat('...'));
        else loadingContext.closeLoading();
    }, [isUploading]);

    useEffect(() => {
        if (isEmpty(loadingContext)) return;
        if (isFetchingContactsToReasign)
            loadingContext.openLoading(t('Loading').concat('...'));
        else loadingContext.closeLoading();
    }, [isFetchingContactsToReasign]);

    const handlePrepareUpload = (file) => uploadFile(file, filterData);

    return (
        <>
            <MaterialContainer maxWidth={false}>
                <HeaderContainer>
                    <Grid size="grow">
                        <TitleIcon title={t('Contact', { count: 2 })} />
                    </Grid>

                    <Grid sx={{ textAlign: 'right' }}>
                        <Button
                            endIcon={<AddIcon />}
                            onClick={() => handleClickCreate()}
                            type="button"
                            visible={filterData?.status === 'active'}
                        >
                            {t('Add')}
                        </Button>
                    </Grid>
                </HeaderContainer>

                <Content
                    contacts={contacts}
                    downloadFile={downloadFile}
                    filterContacts={filterContacts}
                    filterData={filterData}
                    handleClickBulkItems={handleClickBulkItems}
                    handleClickCreate={handleClickCreate}
                    handleSaveRow={handleSaveRow}
                    isFetching={isInitializing || isFetchingContacts}
                    paginationData={paginationData}
                    setFilterData={setFilterData}
                    showEmptyState={showEmptyState}
                    tableSettings={tableSettings}
                    uploadFile={handlePrepareUpload}
                    users={users}
                />
            </MaterialContainer>

            <createContactModal.Container
                onSuccessSave={() => filterContacts(filterData)}
            />

            <Dialog
                onClose={() => closeReasignModal()}
                open={isOpenReasignModal}
                size="sm"
                title={t('Assign new user')}
            >
                <ReasignForm filterData={filterData} />
            </Dialog>
        </>
    );
};

Container.propTypes = {
    closeReasignModal: PropTypes.func,
    contacts: PropTypes.array,
    downloadFile: PropTypes.func,
    filterContacts: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickCreate: PropTypes.func,
    handleSaveRow: PropTypes.func,
    initialize: PropTypes.func,
    isDownloading: PropTypes.bool,
    isFetchingContacts: PropTypes.bool,
    isFetchingContactsToReasign: PropTypes.bool,
    isInitializing: PropTypes.bool,
    isOpenReasignModal: PropTypes.bool,
    isUploading: PropTypes.bool,
    paginationData: PropTypes.object,
    reset: PropTypes.func,
    showEmptyState: PropTypes.object,
    tableSettings: PropTypes.array,
    uploadFile: PropTypes.func,
    users: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    contacts: selectors.getContactsData,
    isDownloading: selectors.getIsDownloading,
    isFetchingContacts: selectors.getIsFetchingContacts,
    isInitializing: selectors.getIsInitializing,
    isOpenReasignModal: selectors.getIsOpenReasignModal,
    isUploading: selectors.getIsUploading,
    paginationData: selectors.getContactsPagination,
    showEmptyState: selectors.getShowEmptyState,
    tableSettings: customColumnsSelectors.getTableSettingsData,
    users: selectors.getUsersData,
});

const mapDispatchToProps = (dispatch) => ({
    closeReasignModal: () => dispatch(actions.closeReasignModal()),
    downloadFile: () => dispatch(actions.downloadFile()),
    filterContacts: (filterData) =>
        dispatch(actions.filterContacts(filterData)),
    handleClickBulkItems: (ids, value, filterData, callback) =>
        dispatch(actions.prepareBulkItems(ids, value, filterData, callback)),
    handleClickCreate: () =>
        dispatch(createContactModalActions.prepareCreate()),
    handleSaveRow: (item) => dispatch(actions.updateProduct(item)),
    initialize: (filterData) => dispatch(actions.initialFetching(filterData)),
    reset: () => dispatch(actions.reset()),
    uploadFile: (file, filterData) =>
        dispatch(actions.uploadFile(file, filterData)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(Container);
